import * as React from 'react';
import BPromise from 'bluebird';
import { History, Location } from 'history';
import { parse } from 'query-string';
import { Grid2 } from 'Src/components/Grid/Grid2';
import { Loader } from 'Src/components/Loader/Loader';
import { ActionItem } from 'Src/components/ActionsButton/ActionsButton';
import { DATE_INTERVAL, FILTER_TYPE, FIRST_PAGE } from 'Src/consts/table';
import { getSelectOptionForAge } from 'Src/helpers/table/select';
import {
  ColumnDefinition,
  getFilters2,
  getOrder,
  getSearchFilter,
  getSearchOrder,
  getServerQueryFilter2,
  isFilterExist2,
  isSortExist,
  TABLE_SORT_DIRECTION
} from 'Src/helpers/table/table';
import { SchoolForm } from 'Src/models/form';
import {
  createSuperAdminSchoolForm,
  deleteSuperAdminSchoolForm,
  getAllSuperAdminSchoolForms,
  getSuperAdminSchoolForms,
  getSuperAdminSchoolFormsCount,
  updateSuperAdminSchoolForm,
  updateSuperAdminSchoolFormStatus
} from 'Src/helpers/service/superadmin/forms';
import './SuperAdminSchoolForms.css';

const COLUMNS: ColumnDefinition[] = [
  {
    text: 'Name',
    field: 'name',
    isSort: true,
    type: FILTER_TYPE.TEXT,
    accessor: ['name']
  },
  {
    text: 'Age Group',
    field: 'ageGroup',
    isSort: false,
    type: FILTER_TYPE.MULTISELECT,
    accessor: ['ageGroup']
  },
  {
    text: 'Status',
    field: 'status',
    isSort: false,
    type: FILTER_TYPE.MULTISELECT,
    accessor: ['status']
  },
  {
    text: 'Created',
    field: 'createdAt',
    isSort: true,
    type: FILTER_TYPE.NONE,
    accessor: ['createdAt']
  }
];

interface Props {
  schoolId: string;
  school: any;
  history: History;
  location: Location;
}

interface FormEditorState {
  id: string;
  name: string;
  age: string;
  pic: string;
  status: string;
  originalStatus: string;
}

interface State {
  items: SchoolForm[];
  allForms: SchoolForm[];
  currentPage: number;
  selectedItems: SchoolForm[];
  sortDirection: TABLE_SORT_DIRECTION;
  sortColumnsName: string;
  isShowFilter: boolean;
  filters: any;
  isDataFiltered: boolean;
  isLoading: boolean;
  isSaving: boolean;
  total: number;
  basePath: string;
  isSelectAllChecked: boolean;
  errorMessage: string;
  successMessage: string;
  isViewPanelOpen: boolean;
  isCreatePanelOpen: boolean;
  isEditPanelOpen: boolean;
  form: FormEditorState;
}

const STATUS_OPTIONS = [
  { text: 'Active', value: 'ACTIVE' },
  { text: 'Archived', value: 'ARCHIVED' },
  { text: 'Removed', value: 'REMOVED' }
];

const EMPTY_FORM: FormEditorState = {
  id: '',
  name: '',
  age: '',
  pic: '',
  status: 'ACTIVE',
  originalStatus: 'ACTIVE'
};

function getFormId(form?: SchoolForm): string {
  return form ? form.id || (form as any)._id || '' : '';
}

function getFormPic(form?: SchoolForm): string {
  return form && (form as any).pic ? (form as any).pic : '';
}

function getFormStatus(form?: SchoolForm): string {
  return form && form.status ? form.status : 'ACTIVE';
}

function formToEditorState(form: SchoolForm): FormEditorState {
  const status = getFormStatus(form);

  return {
    id: getFormId(form),
    name: form.name || '',
    age: typeof form.age === 'number' ? String(form.age) : '',
    pic: getFormPic(form),
    status,
    originalStatus: status
  };
}

function formatDateTime(value?: string): string {
  if (!value) {
    return '';
  }

  const date = new Date(value);

  if (isNaN(date.getTime())) {
    return '';
  }

  return date.toLocaleString('en-GB', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
}

function validateForm(form: FormEditorState): string {
  const age = Number(form.age);

  if (!form.name.trim()) {
    return 'Please enter form name.';
  }

  if (form.age === '' || isNaN(age)) {
    return 'Please enter age as a number.';
  }

  if (age < 0 || age > 30) {
    return 'Age should be between 0 and 30.';
  }

  return '';
}

function getFormsWhere(filters: any): any {
  const where: any = {};
  const name = filters.name;
  const ageGroup = filters.ageGroup;
  const status = filters.status;

  if (typeof name === 'string' && name.trim() !== '') {
    where.name = {
      like: name.trim(),
      options: 'i'
    };
  }

  if (Array.isArray(ageGroup) && ageGroup.length > 0) {
    where.age = {
      $in: ageGroup.map(value => Number(value)).filter(value => !isNaN(value))
    };
  }

  if (Array.isArray(status) && status.length > 0) {
    where.status = {
      $in: status
    };
  }

  return where;
}

export class SuperAdminSchoolForms extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);

    this.state = {
      items: [],
      allForms: [],
      currentPage: FIRST_PAGE,
      selectedItems: [],
      sortDirection: '',
      sortColumnsName: '',
      isShowFilter: false,
      filters: {},
      isDataFiltered: false,
      isLoading: false,
      isSaving: false,
      total: 0,
      basePath: '',
      isSelectAllChecked: false,
      errorMessage: '',
      successMessage: '',
      isViewPanelOpen: false,
      isCreatePanelOpen: false,
      isEditPanelOpen: false,
      form: { ...EMPTY_FORM }
    };
  }

  componentDidMount() {
    this.setState({ isLoading: true, errorMessage: '', successMessage: '' });
    this.setAdditionalItems().then(() => this.setItems());
  }

  UNSAFE_componentWillReceiveProps(nextProps: Props) {
    if (nextProps.location.search !== this.props.location.search || nextProps.schoolId !== this.props.schoolId) {
      this.setState({ isLoading: true, errorMessage: '', successMessage: '' });
      this.setAdditionalItems(nextProps).then(() => this.setItems(nextProps));
    }
  }

  setAdditionalItems(props: Props = this.props): Promise<boolean> {
    return getAllSuperAdminSchoolForms(props.schoolId)
      .then(forms => {
        this.setState({ allForms: forms });
        return true;
      })
      .catch(error => {
        console.error('Can not load superadmin school forms for filters', error);
        this.setState({
          isLoading: false,
          errorMessage: 'Can not load forms. Please check the session and try again.'
        });
        return false;
      });
  }

  setItems(props: Props = this.props) {
    const { history, location, schoolId } = props;
    const search = parse(location.search);
    const page = typeof search.page !== 'undefined' ? Number(search.page) : FIRST_PAGE;

    let sortDirection: TABLE_SORT_DIRECTION = '';
    let sortColumnsName = '';

    if (typeof search.order === 'string') {
      [sortColumnsName, sortDirection] = search.order.split(':') as [string, TABLE_SORT_DIRECTION];
    }

    const sortByFieldExist = sortColumnsName !== '';
    const sortDirectionExist = sortDirection !== '';
    const filters = getFilters2(COLUMNS, search);
    const isShowFilter = isFilterExist2(filters);
    const isDataFiltered = isFilterExist2(filters);
    const where = getFormsWhere(filters);
    const order = sortByFieldExist && sortDirectionExist ? `${sortColumnsName} ${sortDirection}` : 'name ASC';
    const serverQueryFilter = getServerQueryFilter2(page, where, order);

    return Promise.all([
      getSuperAdminSchoolFormsCount(schoolId, where),
      getSuperAdminSchoolForms(schoolId, serverQueryFilter)
    ])
      .then(([countObj, items]) => {
        this.setState({
          currentPage: page,
          sortDirection,
          sortColumnsName,
          isShowFilter,
          isDataFiltered,
          filters,
          items,
          total: countObj.count,
          basePath: history.location.pathname,
          isLoading: false,
          errorMessage: '',
          selectedItems: [],
          isSelectAllChecked: false
        });

        return true;
      })
      .catch(error => {
        console.error('Can not load superadmin school forms', error);
        this.setState({
          isLoading: false,
          errorMessage: 'Can not load forms. Please check the session and try again.'
        });
      });
  }

  refreshForms = (successMessage?: string): void => {
    this.setState(
      {
        isSaving: false,
        isCreatePanelOpen: false,
        isEditPanelOpen: false,
        isViewPanelOpen: false,
        form: { ...EMPTY_FORM },
        successMessage: successMessage || '',
        errorMessage: '',
        selectedItems: [],
        isSelectAllChecked: false
      },
      () => this.setAdditionalItems().then(() => this.setItems())
    );
  };

  setCurrentPageParams = (currentPage: number): void => {
    const search: string[] = [];
    const { filters, sortColumnsName, sortDirection } = this.state;

    if (currentPage !== FIRST_PAGE) {
      search.push(`page=${currentPage}`);
    }

    if (isSortExist(sortDirection, sortColumnsName)) {
      search.push(getSearchOrder(sortDirection, sortColumnsName));
    }

    if (isFilterExist2(filters)) {
      search.push(getSearchFilter(filters));
    }

    this.props.history.push({
      pathname: this.state.basePath,
      search: search.join('&')
    });
  };

  onItemClick = (index: number): void => {
    const { items, selectedItems } = this.state;
    const selectedItem = items[index];
    const selectedItemId = getFormId(selectedItem);
    const selectedItemIndex = selectedItems.findIndex(item => getFormId(item) === selectedItemId);
    const selectedItemsUpdated = [...selectedItems];

    if (selectedItemIndex !== -1) {
      selectedItemsUpdated.splice(selectedItemIndex, 1);
    } else {
      selectedItemsUpdated.push(selectedItem);
    }

    this.setState({ selectedItems: selectedItemsUpdated, successMessage: '' });
  };

  onSelectAllOnPageClick = (): void => {
    const { items, selectedItems } = this.state;
    const itemsFiltered = items.filter(item =>
      selectedItems.every(selectedItem => getFormId(selectedItem) !== getFormId(item))
    );

    this.setState({
      selectedItems: [...selectedItems, ...itemsFiltered],
      isSelectAllChecked: true,
      successMessage: ''
    });
  };

  onSelectAllClick = (): void => {
    const { selectedItems, filters } = this.state;
    const where = getFormsWhere(filters);
    this.setState({ isLoading: true, errorMessage: '', successMessage: '' });

    getAllSuperAdminSchoolForms(this.props.schoolId, where)
      .then(items => {
        const itemsFiltered = items.filter(item =>
          selectedItems.every(selectedItem => getFormId(selectedItem) !== getFormId(item))
        );

        this.setState({
          selectedItems: [...selectedItems, ...itemsFiltered],
          isSelectAllChecked: true,
          isLoading: false
        });
      })
      .catch(error => {
        console.error('Can not select all superadmin school forms', error);
        this.setState({
          isLoading: false,
          errorMessage: 'Can not select all forms. Please try again.'
        });
      });
  };

  onUnselectAllClick = (): void => {
    this.setState({
      selectedItems: [],
      isSelectAllChecked: false,
      successMessage: ''
    });
  };

  onTableSortClick = (sortField: string): void => {
    const { sortDirection, sortColumnsName, filters } = this.state;
    const order = getOrder(sortField, sortDirection, sortColumnsName);
    const search: string[] = [`order=${sortField}:${order}`];

    if (isFilterExist2(filters)) {
      search.push(getSearchFilter(filters));
    }

    this.props.history.push({
      pathname: this.state.basePath,
      search: search.join('&')
    });
  };

  onTableFilterChange = (event, filterField: string, options?): void => {
    const filterValue = event.target.value;
    const filters = this.state.filters;
    const currentFilterField = filters[filterField];
    let nextFilters = { ...filters };

    if (filterField === 'ageGroup') {
      nextFilters.ageGroup = [];
    }

    if (typeof options !== 'undefined') {
      switch (options) {
        case DATE_INTERVAL.FROM:
          nextFilters = {
            ...nextFilters,
            [filterField]: {
              ...currentFilterField,
              from: filterValue
            }
          };
          break;
        case DATE_INTERVAL.TO:
          nextFilters = {
            ...nextFilters,
            [filterField]: {
              ...currentFilterField,
              to: filterValue
            }
          };
          break;
      }
    } else {
      const filter = COLUMNS.find(col => col.field === filterField);
      const filterType = filter ? filter.type : FILTER_TYPE.TEXT;

      if (filterType === FILTER_TYPE.MULTISELECT) {
        const options = event.target.options;
        const value = [];

        for (let i = 0; i < options.length; i++) {
          if (options[i].selected) {
            value.push(options[i].value);
          }
        }

        nextFilters = {
          ...nextFilters,
          [filterField]: value
        };
      } else {
        nextFilters = {
          ...nextFilters,
          [filterField]: filterValue
        };
      }
    }

    this.setState({ filters: nextFilters });
  };

  onApplyFilterClick = (): void => {
    const { filters, sortDirection, sortColumnsName } = this.state;
    const search: string[] = [];

    if (isSortExist(sortDirection, sortColumnsName)) {
      search.push(getSearchOrder(sortDirection, sortColumnsName));
    }

    if (isFilterExist2(filters)) {
      search.push(getSearchFilter(filters));
    }

    this.setState({
      selectedItems: [],
      isSelectAllChecked: false
    });

    this.props.history.push({
      pathname: this.state.basePath,
      search: search.join('&')
    });
  };

  onClearFilterClick = (): void => {
    this.setState({
      selectedItems: [],
      isSelectAllChecked: false
    });

    this.props.history.push({
      pathname: this.state.basePath,
      search: ''
    });
  };

  onTableFilterClick = (event): void => {
    event.preventDefault();

    this.setState({
      isShowFilter: !this.state.isShowFilter
    });
  };

  onCreateFormClick = (): void => {
    this.setState({
      isCreatePanelOpen: true,
      isEditPanelOpen: false,
      isViewPanelOpen: false,
      form: { ...EMPTY_FORM },
      errorMessage: '',
      successMessage: ''
    });
  };

  onViewFormClick = (): void => {
    const selectedItem = this.state.selectedItems[0];

    if (!selectedItem) {
      return;
    }

    this.setState({
      isViewPanelOpen: true,
      isCreatePanelOpen: false,
      isEditPanelOpen: false,
      form: formToEditorState(selectedItem),
      errorMessage: '',
      successMessage: ''
    });
  };

  onEditFormClick = (): void => {
    const selectedItem = this.state.selectedItems[0];

    if (!selectedItem) {
      return;
    }

    this.setState({
      isEditPanelOpen: true,
      isCreatePanelOpen: false,
      isViewPanelOpen: false,
      form: formToEditorState(selectedItem),
      errorMessage: '',
      successMessage: ''
    });
  };

  onDeleteFormsClick = (): void => {
    const selectedItems = this.state.selectedItems;

    if (selectedItems.length === 0) {
      return;
    }

    const message =
      selectedItems.length === 1 ? `Delete form "${selectedItems[0].name}"?` : `Delete ${selectedItems.length} forms?`;

    if (!window.confirm(message)) {
      return;
    }

    this.setState({ isSaving: true, errorMessage: '', successMessage: '' });

    BPromise.all(selectedItems.map(item => deleteSuperAdminSchoolForm(this.props.schoolId, getFormId(item))))
      .then(() => this.refreshForms(selectedItems.length === 1 ? 'Form has been deleted.' : 'Forms have been deleted.'))
      .catch(error => {
        console.error('Can not delete superadmin school forms', error);
        this.setState({
          isSaving: false,
          errorMessage:
            'Can not delete selected form/forms. The form may contain students or be protected by backend rules.'
        });
      });
  };

  onClosePanelClick = (): void => {
    this.setState({
      isViewPanelOpen: false,
      isCreatePanelOpen: false,
      isEditPanelOpen: false,
      form: { ...EMPTY_FORM },
      errorMessage: ''
    });
  };

  onFormChange = (event): void => {
    const { name, value } = event.target;

    this.setState({
      form: {
        ...this.state.form,
        [name]: value
      }
    });
  };

  onSaveCreateFormClick = (): void => {
    const form = this.state.form;
    const validationMessage = validateForm(form);

    if (validationMessage) {
      this.setState({ errorMessage: validationMessage, successMessage: '' });
      return;
    }

    this.setState({ isSaving: true, errorMessage: '', successMessage: '' });

    createSuperAdminSchoolForm(this.props.schoolId, {
      name: form.name.trim(),
      age: Number(form.age),
      pic: form.pic.trim()
    })
      .then(createdForm => {
        if (form.status !== 'ACTIVE') {
          return updateSuperAdminSchoolFormStatus(this.props.schoolId, getFormId(createdForm), form.status);
        }
        return createdForm;
      })
      .then(() => this.refreshForms('Form has been created.'))
      .catch(error => {
        console.error('Can not create superadmin school form', error);
        this.setState({
          isSaving: false,
          errorMessage: 'Can not create form. Please check fields and try again.'
        });
      });
  };

  onSaveEditFormClick = (): void => {
    const form = this.state.form;
    const validationMessage = validateForm(form);

    if (validationMessage) {
      this.setState({ errorMessage: validationMessage, successMessage: '' });
      return;
    }

    this.setState({ isSaving: true, errorMessage: '', successMessage: '' });

    updateSuperAdminSchoolForm(this.props.schoolId, form.id, {
      name: form.name.trim(),
      age: Number(form.age),
      pic: form.pic.trim()
    })
      .then(updatedForm => {
        if (form.status !== form.originalStatus) {
          return updateSuperAdminSchoolFormStatus(this.props.schoolId, getFormId(updatedForm), form.status);
        }
        return updatedForm;
      })
      .then(() => this.refreshForms('Form has been updated.'))
      .catch(error => {
        console.error('Can not update superadmin school form', error);
        this.setState({
          isSaving: false,
          errorMessage: 'Can not update form. Please check fields and try again.'
        });
      });
  };

  renderSelectedFormDetails(): React.ReactNode {
    const selectedItem = this.state.selectedItems[0];

    if (!selectedItem || !this.state.isViewPanelOpen) {
      return null;
    }

    return (
      <div className="SuperAdminSchoolFormsPanel card mt-3 mb-3">
        <div className="card-header d-flex justify-content-between align-items-center">
          <strong>Form details</strong>
          <button className="btn btn-sm btn-outline-secondary" type="button" onClick={this.onClosePanelClick}>
            Close
          </button>
        </div>
        <div className="card-body">
          <div className="SuperAdminSchoolFormsDetailsGrid">
            <div>
              <span className="text-muted">Name</span>
              <strong>{selectedItem.name || '-'}</strong>
            </div>
            <div>
              <span className="text-muted">Age</span>
              <strong>{typeof selectedItem.age === 'number' ? selectedItem.age : '-'}</strong>
            </div>
            <div>
              <span className="text-muted">Age group</span>
              <strong>{selectedItem.ageGroup || '-'}</strong>
            </div>
            <div>
              <span className="text-muted">Status</span>
              <strong>{selectedItem.status || '-'}</strong>
            </div>
            <div>
              <span className="text-muted">Created</span>
              <strong>{formatDateTime(selectedItem.createdAt) || '-'}</strong>
            </div>
            <div>
              <span className="text-muted">ID</span>
              <strong>{getFormId(selectedItem) || '-'}</strong>
            </div>
          </div>

          {getFormPic(selectedItem) && (
            <div className="mt-3">
              <span className="text-muted d-block">Picture</span>
              <div className="SuperAdminSchoolFormsPicValue">{getFormPic(selectedItem)}</div>
            </div>
          )}

          <details className="mt-3">
            <summary>Raw JSON</summary>
            <pre className="SuperAdminSchoolFormsJson">{JSON.stringify(selectedItem, null, 2)}</pre>
          </details>
        </div>
      </div>
    );
  }

  renderFormEditor(isEdit: boolean): React.ReactNode {
    const { form, isSaving } = this.state;

    return (
      <div className="SuperAdminSchoolFormsPanel card mt-3 mb-3">
        <div className="card-header d-flex justify-content-between align-items-center">
          <strong>{isEdit ? 'Edit form' : 'Add form'}</strong>
          <button
            className="btn btn-sm btn-outline-secondary"
            type="button"
            onClick={this.onClosePanelClick}
            disabled={isSaving}
          >
            Close
          </button>
        </div>
        <div className="card-body">
          <div className="form-row">
            <div className="form-group col-md-5">
              <label>Name</label>
              <input
                className="form-control"
                name="name"
                value={form.name}
                onChange={this.onFormChange}
                disabled={isSaving}
              />
            </div>
            <div className="form-group col-md-3">
              <label>Age</label>
              <input
                className="form-control"
                name="age"
                type="number"
                min="0"
                max="30"
                value={form.age}
                onChange={this.onFormChange}
                disabled={isSaving}
              />
            </div>
            <div className="form-group col-md-4">
              <label>Status</label>
              <select
                className="form-control"
                name="status"
                value={form.status}
                onChange={this.onFormChange}
                disabled={isSaving}
              >
                {STATUS_OPTIONS.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.text}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="form-group">
            <label>Picture URI</label>
            <input
              className="form-control"
              name="pic"
              value={form.pic}
              onChange={this.onFormChange}
              disabled={isSaving}
              placeholder="//images.squadintouch.com/..."
            />
            <small className="form-text text-muted">Optional. Keep empty if the form has no image.</small>
          </div>

          <button
            className="btn btn-primary mr-2"
            type="button"
            onClick={isEdit ? this.onSaveEditFormClick : this.onSaveCreateFormClick}
            disabled={isSaving}
          >
            {isSaving ? 'Saving...' : 'Save'}
          </button>
          <button
            className="btn btn-outline-secondary"
            type="button"
            onClick={this.onClosePanelClick}
            disabled={isSaving}
          >
            Cancel
          </button>
        </div>
      </div>
    );
  }

  render() {
    const {
      allForms,
      currentPage,
      errorMessage,
      successMessage,
      filters,
      isDataFiltered,
      isLoading,
      isSaving,
      isSelectAllChecked,
      isShowFilter,
      items,
      selectedItems,
      sortColumnsName,
      sortDirection,
      total,
      isCreatePanelOpen,
      isEditPanelOpen
    } = this.state;

    const ageGroupsNaming = this.props.school.ageGroupsNaming;
    const filterOptions = {
      ageGroup: getSelectOptionForAge(allForms, ageGroupsNaming),
      status: STATUS_OPTIONS
    };

    const hasOneSelectedItem = selectedItems.length === 1;
    const hasSelectedItems = selectedItems.length > 0;
    const actionItems: ActionItem[] = [
      {
        itemText: 'Add form',
        isActive: true,
        onItemClick: this.onCreateFormClick
      },
      {
        itemText: 'View selected form',
        isActive: hasOneSelectedItem,
        onItemClick: this.onViewFormClick
      },
      {
        itemText: 'Edit selected form',
        isActive: hasOneSelectedItem,
        onItemClick: this.onEditFormClick
      },
      {
        itemText: hasSelectedItems && selectedItems.length > 1 ? 'Delete selected forms' : 'Delete selected form',
        isActive: hasSelectedItems,
        onItemClick: this.onDeleteFormsClick
      }
    ];

    if (isLoading) {
      return <Loader />;
    }

    return (
      <div className="SuperAdminSchoolForms">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <div>
            <h4 className="mb-1">Forms</h4>
            <div className="text-muted">Manage school forms/classes.</div>
          </div>
          <button
            className="btn btn-outline-secondary"
            onClick={() => this.setAdditionalItems().then(() => this.setItems())}
          >
            Refresh
          </button>
        </div>

        {errorMessage && <div className="alert alert-danger">{errorMessage}</div>}
        {successMessage && !isLoading && <div className="alert alert-success">{successMessage}</div>}
        {isSaving && <Loader />}

        {this.renderSelectedFormDetails()}
        {isCreatePanelOpen && this.renderFormEditor(false)}
        {isEditPanelOpen && this.renderFormEditor(true)}

        <Grid2
          dataItems={items}
          filters={filters}
          currentPage={currentPage}
          total={total}
          isSelectAllChecked={isSelectAllChecked}
          selectedItemsCount={selectedItems.length}
          isDataFiltered={isDataFiltered}
          sortDirection={sortDirection}
          sortColumnsName={sortColumnsName}
          isShowFilter={isShowFilter}
          dataItemsSelected={selectedItems}
          columns={COLUMNS}
          actionItems={actionItems}
          options={filterOptions}
          onItemClick={this.onItemClick}
          onSortClick={this.onTableSortClick}
          onApplyFilterClick={this.onApplyFilterClick}
          onClearFilterClick={this.onClearFilterClick}
          onTableFilterChange={this.onTableFilterChange}
          onTableFilterClick={this.onTableFilterClick}
          setCurrentPageParams={this.setCurrentPageParams}
          onSelectAllClick={this.onSelectAllClick}
          onSelectAllOnPageClick={this.onSelectAllOnPageClick}
          onUnselectAllClick={this.onUnselectAllClick}
          isCSV={true}
        />
      </div>
    );
  }
}

